import { create } from 'zustand';
import {
    collection,
    collectionGroup,
    deleteDoc,
    doc,
    getDocs,
    onSnapshot,
    query,
    serverTimestamp,
    updateDoc,
    where,
    type Unsubscribe,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import {
    CHANGE_REQUESTS_COLLECTION,
    isPremiumCostRadarTicket,
    type ChangeRequestTicket,
} from '../types/changeRadar';

export type ChangeRadarTicket = ChangeRequestTicket & {
    id: string;
    /** Optional legacy / future field */
    coachId?: string;
};

type SubscribeOpts = {
    /** Admin: alle Tickets; Coach: nur eigene */
    isAdmin: boolean;
    /** Keys zum Matchen gegen betreuer / coachId (id, name, email, …) */
    coachKeys?: string[];
};

type ChangeRadarState = {
    pendingRequests: ChangeRadarTicket[];
    completedRequests: ChangeRadarTicket[];
    isLoading: boolean;
    error: string | null;
    isModalOpen: boolean;
    openModal: () => void;
    closeModal: () => void;
    subscribe: (opts: SubscribeOpts) => void;
    unsubscribe: () => void;
    completeRequest: (
        clientId: string,
        requestId: string,
        userUid: string,
        userName: string
    ) => Promise<void>;
    deleteRequest: (clientId: string, requestId: string) => Promise<void>;
    /** Alle pending Tickets eines Mandanten → completed (Sperr-/Festschreib-Kopplung) */
    resolvePendingForClient: (
        clientId: string,
        coachUid: string,
        coachName: string
    ) => Promise<number>;
};

let unsubAll: Unsubscribe | null = null;
let lastOptsKey = '';

const toMillis = (raw: unknown): number => {
    if (!raw) return 0;
    if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
    if (typeof (raw as { toMillis?: () => number }).toMillis === 'function') {
        try {
            return (raw as { toMillis: () => number }).toMillis();
        } catch {
            return 0;
        }
    }
    if (typeof (raw as { seconds?: number }).seconds === 'number') {
        return (raw as { seconds: number }).seconds * 1000;
    }
    const n = Date.parse(String(raw));
    return Number.isFinite(n) ? n : 0;
};

const mapDoc = (snapDoc: { id: string; data: () => Record<string, unknown>; ref: { parent: { parent: { id: string } | null } } }): ChangeRadarTicket => {
    const data = snapDoc.data() || {};
    const parentClientId = snapDoc.ref.parent.parent?.id || '';
    return {
        ...(data as Omit<ChangeRadarTicket, 'id'>),
        id: snapDoc.id,
        clientId: String(data.clientId || parentClientId || ''),
        mandantNr: String(data.mandantNr || ''),
        firmenname: String(data.firmenname || ''),
        betreuer: data.betreuer != null ? String(data.betreuer) : undefined,
        coachId: data.coachId != null ? String(data.coachId) : undefined,
        status: (data.status as ChangeRadarTicket['status']) || 'pending',
        unlock_h2: data.unlock_h2 === true,
        type: (data.type as ChangeRadarTicket['type']) || 'change_request',
        message: data.message != null ? String(data.message) : undefined,
        senderName: data.senderName != null ? String(data.senderName) : undefined,
        senderEmail: data.senderEmail != null ? String(data.senderEmail) : undefined,
        coachName: data.coachName != null ? String(data.coachName) : undefined,
        coachEmail: data.coachEmail != null ? String(data.coachEmail) : undefined,
        createdAt: data.createdAt,
        completedAt: data.completedAt,
        completedByUid: data.completedByUid != null ? String(data.completedByUid) : undefined,
        completedByName: data.completedByName != null ? String(data.completedByName) : undefined,
        category: data.category != null ? String(data.category) : undefined,
        toolId: data.toolId != null ? String(data.toolId) : undefined,
        toolName: data.toolName != null ? String(data.toolName) : undefined,
        title: data.title != null ? String(data.title) : undefined,
    };
};

const normalizeKey = (v: string) => v.trim().toLowerCase();

/** Ticket gehört zum Coach: coachId (Briefing) ODER betreuer (Ist-Write aus AZ) */
const ticketMatchesCoach = (ticket: ChangeRadarTicket, coachKeys: string[]): boolean => {
    if (!coachKeys.length) return false;
    const keys = coachKeys.map(normalizeKey).filter(Boolean);
    const candidates = [ticket.coachId, ticket.betreuer, ticket.coachEmail, ticket.coachName]
        .filter(Boolean)
        .map((v) => normalizeKey(String(v)));
    return candidates.some((c) =>
        keys.some((k) => c === k || c.includes(k) || k.includes(c))
    );
};

const clearListeners = () => {
    if (unsubAll) {
        unsubAll();
        unsubAll = null;
    }
};

/** Index-/Permission-Fehler → UI-Hinweis, App bleibt nutzbar */
const formatListenerError = (err: unknown, scope: 'pending' | 'completed' | 'all'): string => {
    const raw =
        (err && typeof err === 'object' && 'message' in err && typeof (err as any).message === 'string'
            ? (err as any).message
            : '') || String(err || '');
    const lower = raw.toLowerCase();
    if (
        (lower.includes('requires a') && lower.includes('index')) ||
        lower.includes('collection_group') ||
        lower.includes('not ready yet') ||
        lower.includes('failed_precondition')
    ) {
        return 'Change-Radar: Firestore-Index für change_requests wird noch aufgebaut. Bitte kurz warten und Modal neu öffnen.';
    }
    if (lower.includes('permission') || lower.includes('missing or insufficient')) {
        return 'Change-Radar: Keine Berechtigung für change_requests.';
    }
    return raw || `Change-Radar (${scope}): Listener fehlgeschlagen`;
};

export const useChangeRadarStore = create<ChangeRadarState>((set, get) => ({
    pendingRequests: [],
    completedRequests: [],
    isLoading: false,
    error: null,
    isModalOpen: false,

    openModal: () => set({ isModalOpen: true }),
    closeModal: () => set({ isModalOpen: false }),

    unsubscribe: () => {
        clearListeners();
        lastOptsKey = '';
        set({ pendingRequests: [], completedRequests: [], isLoading: false, error: null });
    },

    subscribe: (opts) => {
        const optsKey = JSON.stringify({
            isAdmin: !!opts.isAdmin,
            coachKeys: (opts.coachKeys || []).map(normalizeKey).sort(),
        });
        if (optsKey === lastOptsKey && unsubAll) return;
        lastOptsKey = optsKey;
        clearListeners();

        set({ isLoading: true, error: null });

        // KEIN where(status) — braucht COLLECTION_GROUP-Index, der oft noch "not ready" ist.
        // Ein Listener auf die Collection-Group, Status/Limit clientseitig.
        unsubAll = onSnapshot(
            collectionGroup(db, CHANGE_REQUESTS_COLLECTION),
            (snap) => {
                let all = snap.docs.map((d) => mapDoc(d as any));
                if (!opts.isAdmin) {
                    all = all.filter((t) => ticketMatchesCoach(t, opts.coachKeys || []));
                }
                const pending = all
                    .filter((t) => t.status === 'pending')
                    .sort((a, b) => toMillis(b.createdAt) - toMillis(a.createdAt));
                const completed = all
                    .filter((t) => t.status === 'completed')
                    .sort((a, b) => toMillis(b.completedAt) - toMillis(a.completedAt))
                    .slice(0, 20);
                set({
                    pendingRequests: pending,
                    completedRequests: completed,
                    isLoading: false,
                    error: null,
                });
            },
            (err) => {
                console.error('changeRadar listener:', err);
                set({
                    isLoading: false,
                    pendingRequests: [],
                    completedRequests: [],
                    error: formatListenerError(err, 'all'),
                });
            }
        );
    },

    completeRequest: async (clientId, requestId, userUid, userName) => {
        if (!clientId || !requestId) throw new Error('clientId/requestId fehlen');
        await updateDoc(
            doc(db, 'kalkulation_mandanten', clientId, CHANGE_REQUESTS_COLLECTION, requestId),
            {
                status: 'completed',
                completedAt: serverTimestamp(),
                completedByUid: userUid || '',
                completedByName: userName || '',
            }
        );
        // Snapshot aktualisiert Listen; optimistisch lokal entfernen
        const { pendingRequests } = get();
        set({
            pendingRequests: pendingRequests.filter((t) => !(t.id === requestId && t.clientId === clientId)),
        });
    },

    deleteRequest: async (clientId, requestId) => {
        if (!clientId || !requestId) throw new Error('clientId/requestId fehlen');
        await deleteDoc(
            doc(db, 'kalkulation_mandanten', clientId, CHANGE_REQUESTS_COLLECTION, requestId)
        );
        const { pendingRequests, completedRequests } = get();
        set({
            pendingRequests: pendingRequests.filter(
                (t) => !(t.id === requestId && t.clientId === clientId)
            ),
            completedRequests: completedRequests.filter(
                (t) => !(t.id === requestId && t.clientId === clientId)
            ),
        });
    },

    resolvePendingForClient: async (clientId, coachUid, coachName) => {
        if (!clientId) return 0;
        const colRef = collection(
            db,
            'kalkulation_mandanten',
            clientId,
            CHANGE_REQUESTS_COLLECTION
        );
        let snap;
        try {
            snap = await getDocs(query(colRef, where('status', '==', 'pending')));
        } catch (err) {
            // Fallback ohne where (falls Index fehlt): clientseitig filtern
            console.warn('resolvePendingForClient where-query:', err);
            snap = await getDocs(colRef);
        }
        // Nur AZ-Änderungen auto-abschließen — Softwarekosten/Abo bleiben manuell im Radar
        const pendingDocs = snap.docs.filter((d) => {
            const data = d.data() || {};
            const st = data.status;
            if (!(st === 'pending' || st == null)) return false;
            return !isPremiumCostRadarTicket({
                category: data.category != null ? String(data.category) : undefined,
                toolId: data.toolId != null ? String(data.toolId) : undefined,
            });
        });
        if (!pendingDocs.length) return 0;

        const payload = {
            status: 'completed' as const,
            completedAt: serverTimestamp(),
            completedByUid: coachUid || '',
            completedByName: coachName || '',
        };
        await Promise.all(pendingDocs.map((d) => updateDoc(d.ref, payload)));

        const doneIds = new Set(pendingDocs.map((d) => d.id));
        const { pendingRequests } = get();
        set({
            pendingRequests: pendingRequests.filter(
                (t) => !(t.clientId === clientId && doneIds.has(t.id))
            ),
        });
        return pendingDocs.length;
    },
}));

export default useChangeRadarStore;
